import type { JSX } from "react";
import type { TriyoDynamicField } from "@/types/models";
import { DealPeoplePicker } from "./DealPeoplePicker";
import {
  DYNAMIC_FIELD_TYPES,
  TOGGLE_OFF,
  TOGGLE_ON,
  parseCheckboxValue,
  serializeCheckboxValue,
  visibleDynamicFields,
} from "./deal-dynamic-fields";

export interface DealDynamicFieldsProps {
  fields: TriyoDynamicField[];
  errors: Record<number, string>;
  /** Applies a new Data value to the field with this Id. */
  onChange: (fieldId: number, data: string) => void;
  disabled?: boolean;
}

function inputId(field: TriyoDynamicField): string {
  return `create-deal-dynamic-${field.Id}`;
}

interface ControlProps {
  field: TriyoDynamicField;
  disabled: boolean;
  invalid: boolean;
  onChange: (data: string) => void;
}

/** Renders the input for one field, matching Triyo.Web's per-FieldType control. */
function DynamicFieldControl({ field, disabled, invalid, onChange }: ControlProps): JSX.Element {
  const value = String(field.Data ?? "");
  const options = (field.LookupValues ?? []).filter(Boolean);
  const common = {
    id: inputId(field),
    disabled,
    "aria-invalid": invalid || undefined,
    "aria-describedby": invalid ? `${inputId(field)}-error` : undefined,
  };

  switch (field.FieldType) {
    case DYNAMIC_FIELD_TYPES.peoplePicker:
      return (
        <DealPeoplePicker
          fieldId={field.Id}
          value={value}
          disabled={disabled}
          invalid={invalid}
          describedBy={invalid ? `${inputId(field)}-error` : undefined}
          onChange={onChange}
        />
      );

    case DYNAMIC_FIELD_TYPES.multiText:
      return (
        <textarea
          {...common}
          value={value}
          rows={3}
          placeholder={field.Placeholder || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case DYNAMIC_FIELD_TYPES.dropDown:
      return (
        <select {...common} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">- Select -</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );

    case DYNAMIC_FIELD_TYPES.radioButtons:
      return (
        <div className="create-deal__dynamic-options" role="radiogroup" aria-label={field.Label ?? undefined}>
          {options.map((option) => (
            <label key={option} className="create-deal__dynamic-option">
              <input
                type="radio"
                name={inputId(field)}
                value={option}
                checked={value === option}
                disabled={disabled}
                onChange={() => onChange(option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );

    case DYNAMIC_FIELD_TYPES.checkbox: {
      const selected = parseCheckboxValue(value);
      return (
        <div className="create-deal__dynamic-options" role="group" aria-label={field.Label ?? undefined}>
          {options.map((option) => (
            <label key={option} className="create-deal__dynamic-option">
              <input
                type="checkbox"
                value={option}
                checked={selected.includes(option)}
                disabled={disabled}
                onChange={() =>
                  onChange(
                    serializeCheckboxValue(
                      selected.includes(option) ? selected.filter((entry) => entry !== option) : [...selected, option]
                    )
                  )
                }
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }

    case DYNAMIC_FIELD_TYPES.toggle:
      return (
        <label className="create-deal__dynamic-toggle">
          <input
            {...common}
            type="checkbox"
            checked={value === TOGGLE_ON}
            onChange={(event) => onChange(event.target.checked ? TOGGLE_ON : TOGGLE_OFF)}
          />
          <span>{value === TOGGLE_ON ? "Yes" : "No"}</span>
        </label>
      );

    case DYNAMIC_FIELD_TYPES.integer:
    case DYNAMIC_FIELD_TYPES.decimal:
      return (
        <input
          {...common}
          type="number"
          step={field.FieldType === DYNAMIC_FIELD_TYPES.integer ? "1" : "any"}
          value={value}
          placeholder={field.Placeholder || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case DYNAMIC_FIELD_TYPES.date:
      return (
        <input
          {...common}
          type="date"
          value={value ? value.slice(0, 10) : ""}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case DYNAMIC_FIELD_TYPES.time:
      return <input {...common} type="time" value={value} onChange={(event) => onChange(event.target.value)} />;

    case DYNAMIC_FIELD_TYPES.url:
      return (
        <input
          {...common}
          type="url"
          value={value}
          placeholder={field.Placeholder || "https://"}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    default:
      return (
        <input
          {...common}
          type="text"
          value={value}
          placeholder={field.Placeholder || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

/**
 * The dynamic fields configured on the selected Deal template. Renders nothing
 * when the template has none, so templates without dynamic fields look exactly
 * as they did before.
 */
export function DealDynamicFields({
  fields,
  errors,
  onChange,
  disabled = false,
}: DealDynamicFieldsProps): JSX.Element | null {
  const shown = visibleDynamicFields(fields);
  if (shown.length === 0) return null;

  return (
    <div className="create-deal__dynamic">
      {shown.map((field) => {
        const error = errors[field.Id];
        return (
          <div key={field.Id} className="create-deal__field">
            <label className="create-deal__field-label" htmlFor={inputId(field)}>
              {field.Label}
              {field.Required ? <span className="create-deal__required"> *</span> : null}
            </label>
            <DynamicFieldControl
              field={field}
              disabled={disabled}
              invalid={!!error}
              onChange={(data) => onChange(field.Id, data)}
            />
            {field.FieldHelp ? <p className="create-deal__dynamic-help">{field.FieldHelp}</p> : null}
            {error ? (
              <p className="create-deal__field-error" id={`${inputId(field)}-error`}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
